"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Check, Play, Sparkles } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Audithero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const todoTasks = [{
    id: 1,
    title: "OTA Channel Audit",
    tag: "Strategy",
    tagColor: "bg-blue-100 text-blue-800",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_singh_headshot.webp"
  }, {
    id: 2,
    title: "Tech Stack Review",
    tag: "Tech",
    tagColor: "bg-amber-100 text-amber-800",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_tech_icon.png"
  }];
  const inProgressTasks = [{
    id: 3,
    title: "Revenue Optimization",
    tag: "Growth",
    tagColor: "bg-blue-600 text-white",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_growth_chart.webp"
  }, {
    id: 4,
    title: "Direct Booking Strategy",
    tag: "Direct",
    tagColor: "bg-amber-600 text-white",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_icon.png"
  }];
  const doneTasks = [{
    id: 5,
    title: "Market Positioning",
    tag: "Complete",
    tagColor: "bg-blue-600 text-white",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/market_analysis_icon.png"
  }];
  const trustItems = ["Data-Driven Insights", "Expert Revenue Management", "Hospitality Tech Specialists"];
  return <section data-section-id="69" id="audithero" className="relative w-full overflow-hidden bg-slate-950 text-slate-50 min-h-[720px] flex items-center">
      {}
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "radial-gradient(hsl(0 0% 100%) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      {}
      <div aria-hidden="true" className="absolute -top-40 -right-40 w-[800px] h-[800px] rounded-lg pointer-events-none" style={{
      background: "radial-gradient(circle, hsla(43, 46%, 55%, 0.20) 0%, transparent 60%)",
      filter: "blur(60px)"
    }} />

      {}
      <div aria-hidden="true" className="absolute -bottom-40 -left-40 w-[700px] h-[700px] rounded-lg pointer-events-none" style={{
      background: "radial-gradient(circle, hsla(222, 47%, 7%, 0.25) 0%, transparent 60%)",
      filter: "blur(80px)"
    }} />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-[55fr_45fr] gap-12 lg:gap-20 items-center">
          {}
          <div ref={leftRef} className={`flex flex-col gap-8 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <Badge variant="outline" className="self-start rounded-lg border-slate-800 bg-slate-900/80 backdrop-blur-sm px-3 py-1.5 text-[13px] font-default text-slate-200">
              <span className="relative flex h-2 w-2 mr-2">
                <span className="absolute inline-flex h-full w-full rounded-lg bg-amber-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-lg h-2 w-2 bg-amber-500" />
              </span>
              EXPERT REVENUE CONSULTING · Sunil Singh
            </Badge>

            {}
            <h1 className="font-default text-5xl md:text-6xl lg:text-7xl xl:text-[84px] font-bold leading-[1.02] tracking-tight text-white">
              Maximize Your Hotel’s Revenue with{" "}
              <span className="bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(135deg, #fbbf24 0%, #d97706 100%)"
            }}>
                Data-Driven OTA Strategies.
              </span>
            </h1>

            {}
            <p className="text-lg md:text-xl text-slate-400 max-w-[520px] leading-relaxed">
              HotelRevBaBa provides expert revenue management, hospitality tech implementation, and comprehensive audits to help your property thrive.
            </p>

            {}
            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              <Link to="/ContactPage">
                <Button size="lg" className="h-12 px-6 rounded-lg bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold text-base shadow-lg shadow-amber-500/20 w-full sm:w-auto">
                  Book a Consultation
                  <ArrowRight className="ml-1 size-4" />
                </Button>
              </Link>
              <Link to="#">
                <Button variant="ghost" size="lg" className="h-12 px-5 rounded-lg text-white hover:bg-slate-800 hover:text-white font-medium text-base w-full sm:w-auto">
                  <span className="flex items-center justify-center size-7 rounded-lg bg-slate-800 mr-1">
                    <Play className="size-3 fill-current" />
                  </span>
                  View Our Services
                </Button>
              </Link>
            </div>

            {}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-2 font-default text-[13px] text-slate-400">
              {trustItems.map((item, i) => <div data-index={i} key={item} className="flex items-center gap-1.5">
                  <Check className="size-3.5 text-amber-500" strokeWidth={2.5} />
                  <span>{item}</span>
                </div>)}
            </div>
          </div>

          {}
          <div ref={rightRef} className={`relative w-full transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="relative mx-auto w-full max-w-[580px] lg:rotate-[-2deg] origin-center" style={{
            filter: "drop-shadow(0 30px 60px hsla(0, 0%, 0%, 0.5))"
          }}>
              <Card className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden p-0 gap-0">
                {}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-slate-950">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <span className="size-2.5 rounded-lg bg-red-500" />
                      <span className="size-2.5 rounded-lg bg-amber-500" />
                      <span className="size-2.5 rounded-lg bg-blue-600" />
                    </div>
                    <span className="ml-3 font-default text-xs text-slate-500">
                      hotelrevbaba / revenue-dashboard
                    </span>
                  </div>
                  <span className="font-default text-xs text-slate-500">K</span>
                </div>

                <CardContent className="p-4 grid grid-cols-3 gap-3">
                  {}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between px-1">
                      <span className="font-default text-xs font-bold text-white uppercase tracking-wider">
                        Audit
                      </span>
                      <span className="font-default text-xs text-slate-500">{todoTasks.length}</span>
                    </div>
                    {todoTasks.map((task, i) => <div data-index={i} key={task.id} className="bg-slate-800 border border-slate-700 rounded-lg p-2.5 flex flex-col gap-2">
                        <p className="text-[11px] text-white leading-snug font-bold">
                          {task.title}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-lg font-default ${task.tagColor}`}>
                            {task.tag}
                          </span>
                          <SafeHydrate><Avatar className="size-5 border border-slate-900">
                            <AvatarImage src={task.avatar} alt="Task owner" />
                            <AvatarFallback className="text-[8px]">SS</AvatarFallback>
                          </Avatar></SafeHydrate>
                        </div>
                      </div>)}
                  </div>

                  {}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between px-1">
                      <span className="font-default text-xs font-bold text-white uppercase tracking-wider">
                        Active
                      </span>
                      <span className="font-default text-xs text-slate-500">{inProgressTasks.length}</span>
                    </div>
                    {inProgressTasks.map((task, i) => <div data-index={i} key={task.id} className="bg-slate-800 border-l-2 border-l-amber-500 border border-slate-700 rounded-lg p-2.5 flex flex-col gap-2">
                        <p className="text-[11px] text-white leading-snug font-bold">
                          {task.title}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-lg font-default ${task.tagColor}`}>
                            {task.tag}
                          </span>
                          <SafeHydrate><Avatar className="size-5 border border-slate-900">
                            <AvatarImage src={task.avatar} alt="Task owner" />
                            <AvatarFallback className="text-[8px]">SS</AvatarFallback>
                          </Avatar></SafeHydrate>
                        </div>
                      </div>)}
                  </div>

                  {}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between px-1">
                      <span className="font-default text-xs font-bold text-white uppercase tracking-wider">
                        Done
                      </span>
                      <span className="font-default text-xs text-slate-500">{doneTasks.length}</span>
                    </div>
                    {doneTasks.map((task, i) => <div data-index={i} key={task.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-2.5 flex flex-col gap-2 opacity-80">
                        <p className="text-[11px] text-white leading-snug font-bold line-through decoration-slate-500/50">
                          {task.title}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-lg font-default ${task.tagColor}`}>
                            <Check className="inline size-2 mr-0.5" strokeWidth={3} />
                            {task.tag}
                          </span>
                          <SafeHydrate><Avatar className="size-5 border border-slate-900">
                            <AvatarImage src={task.avatar} alt="Task owner" />
                            <AvatarFallback className="text-[8px]">SS</AvatarFallback>
                          </Avatar></SafeHydrate>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </div>

            {}
            <div className="hidden md:block absolute -top-6 -right-2 lg:-right-8 w-[240px] lg:rotate-[4deg] origin-center z-20" style={{
            filter: "drop-shadow(0 20px 40px hsla(0, 0%, 0%, 0.5))"
          }}>
              <Card className="bg-slate-950 border border-amber-500/30 rounded-lg p-0 gap-0">
                <CardContent className="p-3.5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center justify-center size-6 rounded-lg bg-amber-500/20">
                      <Sparkles className="size-3 text-amber-500" />
                    </div>
                    <span className="font-default text-xs font-bold text-white">
                      Revenue Insight
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed mb-2">
                    Optimized OTA listings increased <span className="text-amber-500 font-bold">direct bookings</span> by 24% this quarter.
                  </p>
                  <div className="flex items-center gap-1.5 font-default text-[10px] text-slate-500">
                    <span className="size-1.5 rounded-lg bg-amber-500 animate-pulse" />
                    Updated 2 min ago
                  </div>
                </CardContent>
              </Card>
            </div>

            {}
            <div className="hidden md:block absolute -bottom-6 -left-2 lg:-left-10 w-[220px] lg:rotate-[-3deg] origin-center z-20" style={{
            filter: "drop-shadow(0 20px 40px hsla(0, 0%, 0%, 0.5))"
          }}>
              <Card className="bg-slate-950 border border-blue-600/40 rounded-lg p-0 gap-0">
                <CardContent className="p-3.5">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="font-default text-xs font-bold text-white">
                      Revenue Growth
                    </span>
                    <span className="font-default text-[10px] text-blue-500">+24%</span>
                  </div>
                  <div className="flex items-end gap-1 h-12">
                    {[40, 55, 35, 65, 50, 75, 90].map((h, i) => <div data-index={i} key={i} className="flex-1 rounded-lg" style={{
                    height: `${h}%`,
                    background: i === 6 ? "linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%)" : "#334155"
                  }} />)}
                  </div>
                  <div className="flex justify-between mt-1.5 font-default text-[9px] text-slate-500">
                    <span>Mon</span>
                    <span>Sun</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}